import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { listingsAPI, messagesAPI, subscriptionAPI, savedListingsAPI, savedSearchesAPI, analyticsAPI, paymentsAPI } from '../lib/api';
import { ListingGrid } from '../components/listings/ListingGrid';
import { formatPrice, formatDate, getStatusBadgeColor, getStatusLabel, getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Package,
  Heart,
  Search,
  MessageSquare,
  CreditCard,
  Settings,
  FileText,
  CheckCircle,
  Clock,
  Archive,
  TrendingUp,
  Users,
  DollarSign,
  Eye,
  Edit,
  Trash2,
  Bell,
  BellOff,
  BarChart3,
  ExternalLink,
  Play,
  FileSpreadsheet,
  CreditCard as CreditCardIcon,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [myListings, setMyListings] = useState([]);
  const [savedListings, setSavedListings] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Set active tab based on URL path
  const getInitialTab = () => {
    if (location.pathname === '/saved') return 'saved';
    return 'overview';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Sync tab with URL when location changes
  useEffect(() => {
    if (location.pathname === '/saved') {
      setActiveTab('saved');
    } else if (location.pathname === '/dashboard') {
      setActiveTab('overview');
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [savedListingsRes, savedSearchesRes, messagesRes, subscriptionRes] = await Promise.all([
          savedListingsAPI.getAll(),
          savedSearchesAPI.getAll(),
          messagesAPI.getInbox(),
          subscriptionAPI.get(),
        ]);
        
        setSavedListings(savedListingsRes.data);
        setSavedSearches(savedSearchesRes.data);
        setMessages(messagesRes.data);
        setSubscription(subscriptionRes.data);
        
        // Fetch listings for sellers/dealers
        if (user?.role === 'seller' || user?.role === 'dealer') {
          // Safety net: reconcile any payments that completed while the user
          // was off the polling page. Then refetch listings.
          try {
            await paymentsAPI.reconcile();
          } catch (err) {
            // non-blocking
            console.error('Payment reconcile failed:', err);
          }
          const listingsRes = await listingsAPI.getMyListings();
          setMyListings(listingsRes.data);
          try {
            const analyticsRes = await analyticsAPI.getSellerAnalytics();
            setAnalytics(analyticsRes.data);
          } catch (err) {
            console.error('Error loading analytics:', err);
          }
          try {
            const quotaRes = await paymentsAPI.getQuota();
            setQuota(quotaRes.data);
          } catch (err) {
            console.error('Error loading quota:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('¿Eliminar este anuncio? Esta acción no se puede deshacer.')) return;
    
    try {
      await listingsAPI.delete(listingId);
      setMyListings(prev => prev.filter(l => l.id !== listingId));
      toast.success('Anuncio eliminado');
    } catch (error) {
      toast.error('No se pudo eliminar el anuncio');
    }
  };

  const handlePublishListing = async (listing) => {
    // If already paid (within free quota or paid before), just toggle status -> active.
    if (listing.paid !== false) {
      try {
        await listingsAPI.update(listing.id, { status: 'active' });
        setMyListings(prev => prev.map(l => l.id === listing.id ? { ...l, status: 'active' } : l));
        toast.success('¡Anuncio publicado!');
      } catch (error) {
        toast.error(getErrorMessage(error, 'No se pudo publicar el anuncio'));
      }
      return;
    }
    // Needs payment first → start Stripe Checkout.
    try {
      toast.info('Redirigiéndote al pago...');
      const res = await paymentsAPI.createListingCheckout(listing.id, window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo iniciar el pago'));
    }
  };

  const handleDeleteSavedSearch = async (searchId) => {
    try {
      await savedSearchesAPI.delete(searchId);
      setSavedSearches(prev => prev.filter(s => s.id !== searchId));
      toast.success('Búsqueda eliminada');
    } catch (error) {
      toast.error('No se pudo eliminar la búsqueda');
    }
  };

  const handleToggleAlert = async (searchId, currentValue) => {
    try {
      await savedSearchesAPI.update(searchId, { alert_enabled: !currentValue });
      setSavedSearches(prev => prev.map(s => 
        s.id === searchId ? { ...s, alert_enabled: !currentValue } : s
      ));
      toast.success(!currentValue ? 'Alertas activadas' : 'Alertas desactivadas');
    } catch (error) {
      toast.error('No se pudieron actualizar las alertas');
    }
  };

  const handleRunSearch = (search) => {
    const params = new URLSearchParams();
    Object.entries(search.filters_json || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, value.toString());
      }
    });
    navigate(`/browse?${params.toString()}`);
  };

  const formatSearchFilters = (filters) => {
    const parts = [];
    if (filters.category) parts.push(filters.category);
    if (filters.make) parts.push(filters.make);
    if (filters.model) parts.push(filters.model);
    if (filters.search) parts.push(`"${filters.search}"`);
    if (filters.price_max) parts.push(`< $${filters.price_max.toLocaleString()}`);
    if (filters.price_min) parts.push(`> $${filters.price_min.toLocaleString()}`);
    if (filters.year_min) parts.push(`${filters.year_min}+`);
    if (filters.state) parts.push(filters.state);
    return parts.length > 0 ? parts.join(' • ') : 'Todos los anuncios';
  };

  const isSeller = user?.role === 'seller' || user?.role === 'dealer';
  
  const stats = {
    activeListings: myListings.filter(l => l.status === 'active').length,
    draftListings: myListings.filter(l => l.status === 'draft').length,
    soldListings: myListings.filter(l => l.status === 'sold').length,
    savedListingsCount: savedListings.length,
    unreadMessages: messages.filter(m => !m.read).length,
    savedSearchesCount: savedSearches.length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary p-8">
        <div className="max-w-[1400px] mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight" data-testid="dashboard-title">
                Panel{user?.role ? <span className="text-accent"> · {{
                  buyer: 'Comprador',
                  seller: 'Vendedor',
                  dealer: 'Concesionario',
                  admin: 'Administrador',
                }[user.role] || user.role.toUpperCase()}</span> : null}
              </h1>
              <p className="text-muted-foreground">
                Bienvenido, {user?.name}
              </p>
            </div>
            {isSeller && (
              <Link to="/listings/create" data-testid="create-listing-btn">
                <Button className="btn-accent">
                  <Plus className="h-4 w-4 mr-2" />Crear anuncio</Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {isSeller && (
            <>
              <div className="stat-card" data-testid="stat-active">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide">Anuncios activos</p>
                    <p className="text-3xl font-bold font-heading">{stats.activeListings}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-sm">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-draft">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide">Borradores</p>
                    <p className="text-3xl font-bold font-heading">{stats.draftListings}</p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded-sm">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-sold">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide">Vendido</p>
                    <p className="text-3xl font-bold font-heading">{stats.soldListings}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-sm">
                    <Archive className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="stat-card" data-testid="stat-saved">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Guardados</p>
                <p className="text-3xl font-bold font-heading">{stats.savedListingsCount}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-sm">
                <Heart className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-messages">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Mensajes</p>
                <p className="text-3xl font-bold font-heading">{stats.unreadMessages}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-sm">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="w-full overflow-x-auto mb-6 -mx-1 px-1">
            <TabsList className="inline-flex w-max min-w-full">
              <TabsTrigger value="overview" className="uppercase">Resumen</TabsTrigger>
              {isSeller && <TabsTrigger value="listings" className="uppercase">Mis anuncios</TabsTrigger>}
              {isSeller && <TabsTrigger value="analytics" className="uppercase" data-testid="analytics-tab">Analíticas</TabsTrigger>}
              <TabsTrigger value="saved" className="uppercase">Guardados</TabsTrigger>
              <TabsTrigger value="messages" className="uppercase">Mensajes</TabsTrigger>
              {isSeller && <TabsTrigger value="subscription" className="uppercase">Suscripción</TabsTrigger>}
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-white border border-border p-6 rounded-md">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">
                Accesos rápidos
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/browse" className="flex flex-col items-center p-4 bg-secondary rounded-sm hover:bg-accent/10 transition-colors">
                  <Search className="h-8 w-8 mb-2 text-accent" />
                  <span className="text-sm font-medium">Explorar anuncios</span>
                </Link>
                <Link to="/saved" className="flex flex-col items-center p-4 bg-secondary rounded-sm hover:bg-accent/10 transition-colors">
                  <Heart className="h-8 w-8 mb-2 text-accent" />
                  <span className="text-sm font-medium">Guardados</span>
                </Link>
                <Link to="/messages" className="flex flex-col items-center p-4 bg-secondary rounded-sm hover:bg-accent/10 transition-colors">
                  <MessageSquare className="h-8 w-8 mb-2 text-accent" />
                  <span className="text-sm font-medium">Mensajes</span>
                </Link>
                {isSeller && (
                  <Link to="/listings/create" className="flex flex-col items-center p-4 bg-secondary rounded-sm hover:bg-accent/10 transition-colors">
                    <Plus className="h-8 w-8 mb-2 text-accent" />
                    <span className="text-sm font-medium">Nuevo anuncio</span>
                  </Link>
                )}
                {user?.role === 'dealer' && (
                  <Link to="/bulk-upload" className="flex flex-col items-center p-4 bg-secondary rounded-sm hover:bg-accent/10 transition-colors" data-testid="bulk-upload-quick-action">
                    <FileSpreadsheet className="h-8 w-8 mb-2 text-accent" />
                    <span className="text-sm font-medium">Carga masiva</span>
                  </Link>
                )}
              </div>
            </div>

            {/* Recent Messages */}
            <div className="bg-white border border-border p-6 rounded-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Mensajes recientes
                </h2>
                <Link to="/messages" className="text-sm text-accent hover:underline">Ver todos</Link>
              </div>
              {messages.length > 0 ? (
                <div className="space-y-3">
                  {messages.slice(0, 3).map((msg) => (
                    <div key={msg.id} className={`message-item p-4 rounded-sm bg-secondary ${!msg.read ? 'unread' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold">{msg.sender_name}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{msg.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Aún no tienes mensajes</p>
              )}
            </div>
          </TabsContent>

          {/* Listings Tab */}
          {isSeller && (
            <TabsContent value="listings">
              <div className="bg-white border border-border p-4 md:p-6 rounded-md">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                  <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                    Mis anuncios ({myListings.length})
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {user?.role === 'dealer' && (
                      <Link to="/bulk-upload">
                        <Button variant="outline" size="sm" data-testid="bulk-upload-btn">
                          <FileSpreadsheet className="h-4 w-4 mr-2" />Carga masiva</Button>
                      </Link>
                    )}
                    <Link to="/listings/create">
                      <Button className="btn-accent" size="sm">
                        <Plus className="h-4 w-4 mr-2" />Nuevo anuncio</Button>
                    </Link>
                  </div>
                </div>

                {quota && (
                  <div
                    className="flex items-start gap-3 p-4 mb-6 rounded-sm bg-accent/5 border border-accent/20"
                    data-testid="quota-banner"
                  >
                    <CreditCardIcon className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <div className="text-sm flex-1">
                      <p className="font-semibold">
                        Has usado <span data-testid="quota-used">{quota.used}</span> de {quota.free_limit} anuncios gratuitos.
                      </p>
                      <p className="text-muted-foreground">
                        {quota.remaining > 0
                          ? `Te quedan ${quota.remaining} publicaciones gratis.`
                          : `Cada anuncio adicional cuesta ${new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(quota.fee_per_listing)} y se cobra antes de publicar.`}
                      </p>
                    </div>
                  </div>
                )}
                
                {myListings.length > 0 ? (
                  <div className="space-y-3">
                    {myListings.map((listing) => (
                      <div
                        key={listing.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 bg-secondary rounded-sm"
                        data-testid={`my-listing-${listing.id}`}
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <img
                            src={listing.images?.[0]?.image_url || 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=100&h=100&fit=crop'}
                            alt={listing.make}
                            className="w-16 h-14 sm:w-20 sm:h-16 object-cover rounded-sm flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">
                              {listing.year} {listing.make} {listing.model}
                            </h3>
                            <p className="text-sm text-muted-foreground truncate">
                              {formatPrice(listing.price)} • {listing.location_city}, {listing.location_state}
                            </p>
                            <Badge className={`${getStatusBadgeColor(listing.status)} text-white mt-1 sm:hidden`}>
                              {getStatusLabel(listing.status)}
                            </Badge>
                          </div>
                        </div>

                        <Badge className={`${getStatusBadgeColor(listing.status)} text-white hidden sm:inline-flex`}>
                          {getStatusLabel(listing.status)}
                        </Badge>

                        <div className="flex gap-1 justify-end sm:justify-start flex-shrink-0 items-center">
                          {listing.status === 'draft' && (
                            <Button
                              size="sm"
                              className={listing.paid === false ? 'btn-accent text-xs' : 'btn-industrial text-xs'}
                              onClick={() => handlePublishListing(listing)}
                              data-testid={`publish-listing-${listing.id}`}
                            >
                              {listing.paid === false ? (
                                <>
                                  <CreditCardIcon className="h-3.5 w-3.5 mr-1" />
                                  Pagar y publicar
                                </>
                              ) : (
                                'Publicar'
                              )}
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/listings/${listing.id}`)} aria-label="Ver">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/listings/${listing.id}/edit`)} aria-label="Editar">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteListing(listing.id)} aria-label="Eliminar">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-heading text-xl font-bold uppercase mb-2">Aún no tienes anuncios</h3>
                    <p className="text-muted-foreground mb-4">Crea tu primer anuncio para empezar a vender</p>
                    <Link to="/listings/create">
                      <Button className="btn-accent">Crear anuncio</Button>
                    </Link>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Analytics Tab */}
          {isSeller && (
            <TabsContent value="analytics" data-testid="analytics-content">
              <div className="space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="stat-card" data-testid="analytics-total-views">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Vistas totales</p>
                        <p className="text-3xl font-bold font-heading">{analytics?.totals?.views ?? 0}</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-sm flex-shrink-0">
                        <Eye className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card" data-testid="analytics-total-messages">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Mensajes</p>
                        <p className="text-3xl font-bold font-heading">{analytics?.totals?.messages ?? 0}</p>
                      </div>
                      <div className="bg-purple-100 p-3 rounded-sm flex-shrink-0">
                        <MessageSquare className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card" data-testid="analytics-total-conversations">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Conversaciones</p>
                        <p className="text-3xl font-bold font-heading">{analytics?.totals?.conversations ?? 0}</p>
                      </div>
                      <div className="bg-orange-100 p-3 rounded-sm flex-shrink-0">
                        <Users className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card" data-testid="analytics-total-listings">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Anuncios</p>
                        <p className="text-3xl font-bold font-heading">{analytics?.totals?.listings ?? 0}</p>
                      </div>
                      <div className="bg-green-100 p-3 rounded-sm flex-shrink-0">
                        <Package className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Per-listing analytics */}
                <div className="bg-white border border-border p-4 md:p-6 rounded-md">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="h-5 w-5 text-accent" />
                    <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                      Desempeño por anuncio
                    </h2>
                  </div>

                  {analytics?.listings?.length > 0 ? (
                    <div className="space-y-3" data-testid="analytics-listings-rows">
                      {analytics.listings.map((row) => (
                        <div
                          key={row.listing_id}
                          className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 bg-secondary rounded-sm"
                          data-testid={`analytics-row-${row.listing_id}`}
                        >
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <img
                              src={row.thumbnail || 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=100&h=100&fit=crop'}
                              alt={row.title}
                              className="w-16 h-14 sm:w-20 sm:h-16 object-cover rounded-sm flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold truncate">{row.title}</h3>
                              <p className="text-sm text-muted-foreground truncate">
                                {formatPrice(row.price)}
                              </p>
                              <Badge className={`${getStatusBadgeColor(row.status)} text-white mt-1`}>
                                {getStatusLabel(row.status)}
                              </Badge>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2 sm:gap-4 sm:flex-shrink-0 text-center">
                            <div className="px-2">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs uppercase">
                                <Eye className="h-3.5 w-3.5" /> Vistas
                              </div>
                              <p className="font-heading font-bold text-lg" data-testid={`analytics-views-${row.listing_id}`}>{row.views}</p>
                            </div>
                            <div className="px-2">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs uppercase">
                                <MessageSquare className="h-3.5 w-3.5" /> Mens.
                              </div>
                              <p className="font-heading font-bold text-lg" data-testid={`analytics-msgs-${row.listing_id}`}>{row.messages}</p>
                            </div>
                            <div className="px-2">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs uppercase">
                                <Users className="h-3.5 w-3.5" /> Convs.
                              </div>
                              <p className="font-heading font-bold text-lg">{row.conversations}</p>
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/listings/${row.listing_id}`)}
                            aria-label="Ver"
                            className="self-end sm:self-auto"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-heading text-xl font-bold uppercase mb-2">Aún no hay datos</h3>
                      <p className="text-muted-foreground">Crea tus anuncios para empezar a ver estadísticas</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          )}
          <TabsContent value="saved" className="space-y-8">
            {/* Saved Listings */}
            <div className="bg-white border border-border p-4 md:p-6 rounded-md">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">
                Anuncios guardados ({savedListings.length})
              </h2>
              {savedListings.length > 0 ? (
                <ListingGrid listings={savedListings} columns={3} showSaveButton={false} />
              ) : (
                <div className="text-center py-12">
                  <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-heading text-xl font-bold uppercase mb-2">No tienes anuncios guardados</h3>
                  <p className="text-muted-foreground mb-4">Guarda anuncios para verlos más tarde</p>
                  <Link to="/browse">
                    <Button className="btn-accent">Explorar anuncios</Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Saved Searches */}
            <div className="bg-white border border-border p-4 md:p-6 rounded-md">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Búsquedas guardadas ({savedSearches.length})
                </h2>
                <Link to="/browse">
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4 mr-2" />
                    Nueva búsqueda
                  </Button>
                </Link>
              </div>
              {savedSearches.length > 0 ? (
                <div className="space-y-3">
                  {savedSearches.map((search) => (
                    <div
                      key={search.id}
                      className="p-4 bg-secondary rounded-sm"
                      data-testid={`saved-search-${search.id}`}
                    >
                      <div className="flex flex-col gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold truncate">{search.name || 'Búsqueda guardada'}</h3>
                          <p className="text-sm text-muted-foreground mt-1 break-words">
                            {formatSearchFilters(search.filters_json || {})}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Guardada el {formatDate(search.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-sm border border-border">
                            {search.alert_enabled ? (
                              <Bell className="h-3.5 w-3.5 text-accent" />
                            ) : (
                              <BellOff className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                            <Switch
                              checked={search.alert_enabled}
                              onCheckedChange={() => handleToggleAlert(search.id, search.alert_enabled)}
                              className="scale-75"
                              data-testid={`alert-toggle-${search.id}`}
                            />
                            <span className="text-xs text-muted-foreground hidden sm:inline">Alertas</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRunSearch(search)}
                            data-testid={`run-search-${search.id}`}
                          >
                            <Play className="h-3.5 w-3.5 mr-1" />
                            Ejecutar
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 h-8 w-8 ml-auto"
                            onClick={() => handleDeleteSavedSearch(search.id)}
                            aria-label="Eliminar búsqueda"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-heading text-xl font-bold uppercase mb-2">No tienes búsquedas guardadas</h3>
                  <p className="text-muted-foreground mb-4">Guarda tus criterios de búsqueda para encontrar anuncios fácilmente más tarde</p>
                  <Link to="/browse">
                    <Button className="btn-accent">Explorar anuncios</Button>
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white border border-border p-6 rounded-md">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">
                Mensajes ({messages.length})
              </h2>
              {messages.length > 0 ? (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`message-item p-4 rounded-sm bg-secondary ${!msg.read ? 'unread' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{msg.sender_name}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{msg.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-heading text-xl font-bold uppercase mb-2">No tienes mensajes</h3>
                  <p className="text-muted-foreground">Tu bandeja de entrada está vacía</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          {isSeller && (
            <TabsContent value="subscription">
              <div className="bg-white border border-border p-6 rounded-md">
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">
                  Suscripción
                </h2>
                {subscription && (() => {
                  const planPrices = { free: 0, pro: 29.99, enterprise: 99.99 };
                  const planNames = { free: 'Free', pro: 'Pro', enterprise: 'Enterprise' };
                  const currentPlan = subscription.plan || 'free';
                  const currentPrice = planPrices[currentPlan] ?? 0;
                  return (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-secondary rounded-sm">
                      <div>
                        <h3 className="font-heading text-2xl font-bold uppercase">Plan {planNames[currentPlan]}</h3>
                        <p className="text-muted-foreground">
                          {subscription.listing_limit} anuncios incluidos
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {subscription.listings_used ?? 0} de {subscription.listing_limit} utilizados
                        </p>
                      </div>
                      <div className="sm:text-right">
                        <p className="font-heading text-3xl font-bold">{formatPrice(currentPrice)}</p>
                        <p className="text-sm text-muted-foreground">por mes</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      {[
                        { id: 'free', name: 'Free', price: 0, limit: 3 },
                        { id: 'pro', name: 'Pro', price: 29.99, limit: 50 },
                        { id: 'enterprise', name: 'Enterprise', price: 99.99, limit: 500 },
                      ].map((plan) => {
                        const current = currentPlan === plan.id;
                        return (
                        <div key={plan.name} className={`p-6 border-2 rounded-sm ${current ? 'border-accent bg-accent/5' : 'border-border'}`}>
                          <h4 className="font-heading text-xl font-bold uppercase mb-2">{plan.name}</h4>
                          <p className="text-2xl font-bold mb-2">{formatPrice(plan.price)}<span className="text-sm font-normal">/mes</span></p>
                          <p className="text-sm text-muted-foreground mb-4">{plan.limit} anuncios</p>
                          {current ? (
                            <Badge className="bg-accent text-white">Plan actual</Badge>
                          ) : (
                            <Button variant="outline" className="w-full">Mejorar plan</Button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })()}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
